import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  IconButton,
  Divider,
  Tooltip,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  LockClock,
  Download,
  Print,
  Share,
  ArrowBack,
  Refresh,
  Update,
  Assessment,
  School
} from '@mui/icons-material';
import { groupsApi, performanceApi, pdfApi } from '../api';
import { createPerformanceExportUtils } from '../PerformanceExportUtils';
import MembersTable from './MembersTable';
import CashSummary from './CashSummary';
import SavingsSummary from './SavingsSummary';
import AdvanceSummary from './AdvanceSummary';
import LoanSummary from './LoanSummary';
import GroupReport from './GroupReport';
import NextMeetingDetails from './NextMeetingDetails';
import ClosePerformance from '../ClosePerformance';
import { createPerformanceExcelExportUtils } from '../PerformanceExcelExportUtils';
import ExportFormatDialog from '../ExportFormatDialog';
import PerformanceValidation from '../PerformanceValidation';
import { useSchoolFeesMode } from '../SchoolFeesContext';

const PerformanceAnalysis = () => {
  const { groupId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // What PerformancePeriods.js sent us on the way in — the exact filters
  // it had on screen, plus which row to re-highlight. We never read this
  // ourselves; it's only for handing back verbatim via the back button
  // below, so returning restores that view instead of resetting it.
  const returnTo = location.state?.returnTo || null;
  // Present when we arrived from a specific period elsewhere (e.g. the
  // Performance Periods dashboard) rather than the normal "current period
  // for this group" entry point. When set, we display exactly that period
  // and skip the generate/update-on-mount flow below entirely.
  const periodIdParam = searchParams.get('period_id');
  const { getModeForGroup, setModeForGroup } = useSchoolFeesMode();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmCloseForm, setConfirmCloseForm] = useState(false);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Performance analysis data
  const [performanceData, setPerformanceData] = useState(null);
  const [members, setMembers] = useState([]);
  const [summaryData, setSummaryData] = useState(null);

  // Month navigation state
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0);

  // Windowed prev/next navigation — driven by the backend's real
  // has_previous/has_next flags (see group_performance_analysis's
  // period_id branch), not by client-side array bounds. Each prev/next
  // click re-fetches a fresh <=3-period window centered on the neighbour
  // id below, rather than paging through a big pre-loaded list.
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [previousPeriodId, setPreviousPeriodId] = useState(null);
  const [nextPeriodId, setNextPeriodId] = useState(null);

  // The sibling period (opposite account type, same meeting) for whatever
  // period is currently on screen — null when none exists. Powers the
  // "Switch to School Fees"/"Switch to Regular" header button.
  const [counterpartPeriod, setCounterpartPeriod] = useState(null);
  
  // Navigation state to track auto-updates
  const [navigating, setNavigating] = useState(false);
  
  // Export utilities and states
  const [exportUtils, setExportUtils] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [excelExportUtils, setExcelExportUtils] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);

  // ✅ Get mode from context on component mount - this is the source of truth
  const [isSchoolFeesMode, setIsSchoolFeesMode] = useState(() => {
    return getModeForGroup(groupId);
  });
  
  // ✅ Helper to update both local state and context
  const updateSchoolFeesMode = (value) => {
    console.log('🔧 Updating school fees mode:', value);
    setIsSchoolFeesMode(value);
    setModeForGroup(groupId, value);
  };

  // Function to get header colors based on period status
  const getHeaderColors = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return {
          bgcolor: '#FF8F00',
          color: 'white'
        };
      case 'closed':
        return {
          bgcolor: '#424242',
          color: 'white'
        };
      case 'open':
      default:
        return {
          bgcolor: 'primary.main',
          color: 'white'
        };
    }
  };

  // ✅ FIXED: Enhanced fetchInitialData with proper mode handling
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setError(null);
  
        // Fetch group details
        const fetchedGroup = await groupsApi.getGroupDetails(groupId);
        setGroup(fetchedGroup);
  
        // Initialize BOTH export utilities
        const pdfUtils = createPerformanceExportUtils(groupId, fetchedGroup.name);
        setExportUtils(pdfUtils);
        
        const excelUtils = createPerformanceExcelExportUtils(groupId, fetchedGroup.name);
        setExcelExportUtils(excelUtils);

        // ✅ Arrived pointing at one specific, already-generated period
        // (e.g. from the Performance Periods dashboard). Just display it —
        // no generate/update call up front. That flow assumes "the
        // group's current period", which is the wrong thing to trigger
        // here: this period may already be closed, or be the school-fees/
        // regular sibling of whatever the group's current period is.
        // navigateToPeriodId still refreshes it first if it's open/active
        // (see its autoUpdateIfActive default) — just scoped to this one
        // period rather than "the group's current period".
        if (periodIdParam) {
          await navigateToPeriodId(periodIdParam);
          return;
        }
  
        // ✅ FIX: Get mode from context first as default
        let detectedMode = getModeForGroup(groupId);
        console.log('📍 Initial mode from context:', detectedMode);
        
        // ✅ Try to fetch existing data and confirm/detect mode from backend
        try {
          const initialResponse = await performanceApi.getGroupPerformanceAnalysis(groupId, {});
          
          if (initialResponse?.performance_analyses?.length > 0 && 
              initialResponse.performance_analyses[0]?.is_school_fees !== undefined) {
            detectedMode = initialResponse.performance_analyses[0].is_school_fees;
            console.log('🎯 Detected school fees mode from backend:', detectedMode);
            
            // Update both state and context with detected mode
            setIsSchoolFeesMode(detectedMode);
            setModeForGroup(groupId, detectedMode);
          }
        } catch (fetchErr) {
          console.log('📝 No existing data found, will use context mode:', detectedMode);
        }
        
        // ✅ FIX: Now update/generate with the correct mode
        try {
          console.log('🔄 Updating with mode:', detectedMode);
          await performanceApi.updatePerformanceAnalysis(
            groupId,
            {},
            detectedMode
          );
        } catch (updateErr) {
          console.log('⚠️ Update failed, generating new analysis with mode:', detectedMode);
          await performanceApi.generatePerformanceAnalysis(groupId, detectedMode);
        }
        
        // ✅ FIX: Fetch with the detected mode
        const landedPeriod = await fetchPerformanceAnalysis(detectedMode);

        // One extra windowed lookup, scoped to whatever period we just
        // landed on, purely to populate accurate hasPrevious/hasNext/
        // counterpart-period info from the backend (rather than deriving
        // it from the full period list above) — keeps prev/next and the
        // school-fees switch consistent regardless of entry point, without
        // re-running generate/update again.
        if (landedPeriod?.id) {
          await navigateToPeriodId(landedPeriod.id, { autoUpdateIfActive: false, showLoading: false });
        }
  
      } catch (err) {
        console.error('Error fetching initial data:', err);
        setError(err.message || 'Failed to load performance data');
      } finally {
        setLoading(false);
      }
    };
  
    if (groupId) {
      fetchInitialData();
    }
  }, [groupId, periodIdParam]);

  // Applies a windowed group_performance_analysis response (the
  // period_id branch: target period + <=2 same-account-type neighbours)
  // to all the relevant state at once.
  const applyWindowedResponse = (response) => {
    if (!response?.resolved_period) {
      setError('Performance period not found');
      setPerformanceData([]);
      setMembers([]);
      setAvailablePeriods([]);
      setCurrentPeriod(null);
      setSummaryData(null);
      setHasPrevious(false);
      setHasNext(false);
      setPreviousPeriodId(null);
      setNextPeriodId(null);
      setCounterpartPeriod(null);
      return null;
    }

    const resolvedPeriod = response.resolved_period;
    const resolvedMode = resolvedPeriod.is_school_fees ?? response.summary?.is_school_fees ?? false;

    // Keep context/local mode state in sync with the period we actually
    // landed on (it may be the school-fees sibling of whatever mode was
    // last active for this group).
    setIsSchoolFeesMode(resolvedMode);
    setModeForGroup(groupId, resolvedMode);

    setPerformanceData(response.performance_analyses || []);

    const windowPeriods = (response.periods || []).sort((a, b) =>
      new Date(b.period_start) - new Date(a.period_start)
    );
    setAvailablePeriods(windowPeriods);

    const matchIndex = windowPeriods.findIndex(p =>
      p.period_start === resolvedPeriod.period_start &&
      p.period_end === resolvedPeriod.period_end
    );
    const landingIndex = matchIndex !== -1 ? matchIndex : 0;
    const landingPeriod = windowPeriods[landingIndex] || resolvedPeriod;

    setCurrentPeriodIndex(landingIndex);
    setCurrentPeriod(landingPeriod);

    const periodMembers = (response.performance_analyses || [])
      .filter(item =>
        item.period_start === landingPeriod?.period_start &&
        item.period_end === landingPeriod?.period_end
      )
      .sort((a, b) => {
        const idA = parseInt(a.member_reference_id) || 0;
        const idB = parseInt(b.member_reference_id) || 0;
        return idA - idB;
      });

    setMembers(periodMembers);
    generateSummaryData(periodMembers);

    const nav = response.navigation || {};
    setHasPrevious(Boolean(nav.has_previous));
    setHasNext(Boolean(nav.has_next));
    setPreviousPeriodId(nav.previous_period_id ?? null);
    setNextPeriodId(nav.next_period_id ?? null);
    setCounterpartPeriod(response.counterpart_period || null);

    return landingPeriod;
  };

  // Central place all period-by-id navigation goes through: prev/next
  // arrows, the initial period_id landing, and the school-fees switch.
  // Each call is a fresh backend request scoped to <=3 periods around
  // periodId — never the group's whole period history.
  const navigateToPeriodId = async (periodId, { autoUpdateIfActive = true, showLoading = true } = {}) => {
    try {
      if (showLoading) setNavigating(true);

      let response = await performanceApi.getGroupPerformanceAnalysis(groupId, { period_id: periodId });

      if (!response?.resolved_period) {
        applyWindowedResponse(response);
        return null;
      }

      // If this period is still open/active, refresh its numbers first —
      // it may not reflect payments recorded since the last generate/
      // update. Closed periods are left untouched (the backend also
      // no-ops updates on closed periods, but skipping the call here
      // avoids the round trip).
      const periodStatus = response.resolved_period.status;
      if (autoUpdateIfActive && (periodStatus === 'open' || periodStatus === 'active')) {
        try {
          await performanceApi.updatePerformanceAnalysis(
            groupId,
            {
              period_start: response.resolved_period.period_start,
              period_end: response.resolved_period.period_end
            },
            response.resolved_period.is_school_fees
          );
          response = await performanceApi.getGroupPerformanceAnalysis(groupId, { period_id: periodId });
        } catch (updateErr) {
          console.log('⚠️ Could not refresh open/active period before displaying, showing existing data:', updateErr);
        }
      }

      return applyWindowedResponse(response);
    } catch (err) {
      console.error('Error navigating to period:', err);
      setError('Failed to load that period');
      return null;
    } finally {
      if (showLoading) setNavigating(false);
    }
  };

  // ✅ FIXED: Updated fetchPerformanceAnalysis - always use correct mode
  const fetchPerformanceAnalysis = async (modeOverride = null) => {
    try {
      // ✅ Use modeOverride if provided, otherwise get from context
      const currentMode = getModeForGroup(groupId);
      const effectiveMode = modeOverride !== null ? modeOverride : currentMode;
      
      console.log('🔍 Fetching performance analysis with mode:', effectiveMode);
      
      const filters = {};
      if (effectiveMode !== undefined) {
        filters.is_school_fees = effectiveMode;
      }
      
      const response = await performanceApi.getGroupPerformanceAnalysis(groupId, filters);
      
      if (response && response.performance_analyses && response.performance_analyses.length > 0) {
        setPerformanceData(response.performance_analyses);
        
        // ✅ Confirm/update mode from response
        if (response.performance_analyses[0]?.is_school_fees !== undefined) {
          const responseMode = response.performance_analyses[0].is_school_fees;
          if (responseMode !== effectiveMode) {
            console.log('🔄 Mode mismatch - updating context to:', responseMode);
            updateSchoolFeesMode(responseMode);
          }
        }
        
        // Use the periods array from the response
        if (response.periods && response.periods.length > 0) {
          const sortedPeriods = response.periods.sort((a, b) => 
            new Date(b.period_start) - new Date(a.period_start)
          );
          
          setAvailablePeriods(sortedPeriods);
          setCurrentPeriod(sortedPeriods[0]);
          setCurrentPeriodIndex(0);
          
          const currentPeriodMembers = response.performance_analyses
            .filter(item => 
              item.period_start === sortedPeriods[0]?.period_start &&
              item.period_end === sortedPeriods[0]?.period_end
            )
            .sort((a, b) => {
              const idA = parseInt(a.member_reference_id) || 0;
              const idB = parseInt(b.member_reference_id) || 0;
              return idA - idB;
            });
          
          setMembers(currentPeriodMembers);
          generateSummaryData(currentPeriodMembers);
          return sortedPeriods[0] || null;
        } else {
          // Fallback: Extract unique periods from performance analyses
          const uniquePeriods = [];
          const seenPeriods = new Set();
          
          response.performance_analyses.forEach(item => {
            const periodKey = `${item.period_start}_${item.period_end}`;
            if (!seenPeriods.has(periodKey)) {
              seenPeriods.add(periodKey);
              uniquePeriods.push({
                period_start: item.period_start,
                period_end: item.period_end,
                period_name: item.period_name,
                status: item.period_status || 'active'
              });
            }
          });
          
          uniquePeriods.sort((a, b) => new Date(b.period_start) - new Date(a.period_start));
          
          setAvailablePeriods(uniquePeriods);
          setCurrentPeriod(uniquePeriods[0]);
          setCurrentPeriodIndex(0);
          
          const currentPeriodMembers = response.performance_analyses
            .filter(item => 
              item.period_start === uniquePeriods[0]?.period_start &&
              item.period_end === uniquePeriods[0]?.period_end
            )
            .sort((a, b) => {
              const idA = parseInt(a.member_reference_id) || 0;
              const idB = parseInt(b.member_reference_id) || 0;
              return idA - idB;
            });
          
          setMembers(currentPeriodMembers);
          generateSummaryData(currentPeriodMembers);
          // uniquePeriods entries built from performance_analyses don't
          // carry a real period `id` (only period_start/end/name/status),
          // so there's nothing to hand to navigateToPeriodId here — this
          // fallback path only runs when the backend's own 'periods' list
          // was empty, which shouldn't happen in practice.
          return null;
        }
      } else {
        setPerformanceData([]);
        setMembers([]);
        setAvailablePeriods([]);
        setCurrentPeriod(null);
        setSummaryData(null);
        return null;
      }
    } catch (err) {
      console.error('Error fetching performance analysis:', err);
      setError('Failed to fetch performance analysis data');
      return null;
    }
  };
  
  // Generate summary data from members performance data
  const generateSummaryData = (membersData) => {
    const groupTotals = membersData.length > 0 ? membersData[0].group_totals : null;
    
    const totals = membersData.reduce((acc, member) => {
      // Same offset convention as calculateTotals()/the PDF backend: a
      // negative lsf marks an offset member whose LSF-linked columns are
      // excluded from these totals rather than summed in negative.
      const isOffset = (parseFloat(member.lsf) || 0) < 0;
      const totalRepaidValue = parseFloat(member.total_repaid) || 0;

      return {
        totalLSF: acc.totalLSF + (isOffset ? 0 : (parseFloat(member.lsf) || 0)),
        totalAdvancesPaid: acc.totalAdvancesPaid + (isOffset ? 0 : (parseFloat(member.adv_paid) || 0)),
        advanceInterestPaid: acc.advanceInterestPaid + (isOffset ? 0 : (parseFloat(member.int_paid) || 0)),
        loanRepaid: acc.loanRepaid + (isOffset ? 0 : (parseFloat(member.inst_paid) || 0)),
        totalCollection: acc.totalCollection + (totalRepaidValue >= 0 ? totalRepaidValue : 0),
        totalOthers: acc.totalOthers + (isOffset ? 0 : (parseFloat(member.others) || 0)),
        totalAdvCF: acc.totalAdvCF + (isOffset ? 0 : (parseFloat(member.adv_cf) || 0)),
        totalLoanCF: acc.totalLoanCF + (isOffset ? 0 : (parseFloat(member.loan_cf) || 0)),
        totalLSFCF: acc.totalLSFCF + (isOffset ? 0 : (parseFloat(member.lsf_cf) || 0))
      };
    }, {
      totalLSF: 0,
      totalAdvancesPaid: 0,
      advanceInterestPaid: 0,
      loanRepaid: 0,
      totalCollection: 0,
      totalOthers: 0,
      totalAdvCF: 0,
      totalLoanCF: 0,
      totalLSFCF: 0
    });

    // Cash actually collected/paid this period, used for both the cash
    // summary's "Total Cash at Hand" and to back into "Paid via M-Pesa".
    const cashPayments = groupTotals?.total_cash_payments || 0;
    const fundsInOffice = groupTotals?.funds_in_office || 0;
    const paidViaMpesa = totals.totalCollection - (cashPayments + fundsInOffice);

    const cashSummary = {
      totalLSF: totals.totalLSF,
      totalAdvancesPaid: totals.totalAdvancesPaid,
      advanceInterestPaid: totals.advanceInterestPaid,
      loanRepaid: totals.loanRepaid,
      others: totals.totalOthers,
      finesPenalties: groupTotals?.fines_collected || 0,
      totalCollection: groupTotals?.total_cash_collection || totals.totalCollection || 0,
      cashFromOffice: groupTotals?.cash_from_office || 0,
      totalCashAtHand: (cashPayments || totals.totalCollection) + (groupTotals?.cash_from_office || 0),
      lessAdvancesGiven: groupTotals?.adva_given || 0,
      lessLoansGiven: groupTotals?.less_loans_given || 0,
      fundsInOffice,
      netCashRemitted: groupTotals?.net_cash_remitted || 0,
      paidViaMpesa
    };

    // Savings B/F and C/F come from the members table LSF totals (previously
    // calculated but never displayed). Refund / Offset / Proc Charges have no
    // known backend field yet, so they default to 0 until the API exposes
    // them - swap in the real field names once available. "Total" here is
    // Refund + Proc Charges, and "Contributions" is this period's LSF
    // collected from the table, matching the reference layout.
    const savingsRefund = groupTotals?.savings_refund || 0;
    const savingsOffset = groupTotals?.savings_offset || 0;
    const procCharges = groupTotals?.proc_charges || 0;

    const savingsSummary = {
      savingsBF: groupTotals?.savings_before ?? groupTotals?.lsf_before ?? (totals.totalLSFCF - totals.totalLSF),
      savingsRefund,
      savingsOffset,
      procCharges,
      total: savingsRefund + procCharges,
      contributions: totals.totalLSF,
      savingsCF: groupTotals?.savings_cf ?? totals.totalLSFCF
    };

    const advanceSummary = {
      advanceBF: groupTotals?.adv_before || 0,
      advancesGiven: groupTotals?.adva_given || 0,
      intOnAdvGiven: groupTotals?.int_on_adva_given || 0,
      totalPenalties: groupTotals?.advance_penalties || 0,
      total: (groupTotals?.adv_before || 0) + (groupTotals?.adva_given || 0)
        + (groupTotals?.int_on_adva_given || 0) + (groupTotals?.advance_penalties || 0),
      lessAdvancePaid: totals.totalAdvancesPaid,
      advanceOffset: groupTotals?.advance_offset || 0,
      advCF: totals.totalAdvCF
    };

    const loanSummary = {
      loansBF: groupTotals?.loans_before || 0,
      loansGiven: groupTotals?.loans_given || 0,
      totalPenalties: groupTotals?.loan_penalties || 0,
      total: (groupTotals?.loans_before || 0) + (groupTotals?.loans_given || 0)
        + (groupTotals?.loan_penalties || 0),
      lessLoansPaid: totals.loanRepaid,
      loanOffset: groupTotals?.loan_offset || 0,
      netLoanCF: totals.totalLoanCF
    };

    // Next meeting details are not part of performance_analyses at all yet -
    // wiring this up to the real field names once the backend exposes them
    // (e.g. on the group record or the period record) is a follow-up.
    const nextMeeting = {
      date: groupTotals?.next_meeting_date || group?.next_meeting_date || null,
      time: groupTotals?.next_meeting_time || group?.next_meeting_time || null,
      venue: groupTotals?.next_meeting_venue || group?.next_meeting_venue || null,
      contactPhone: groupTotals?.contact_phone || group?.contact_phone || null
    };

    setSummaryData({ cashSummary, savingsSummary, advanceSummary, loanSummary, nextMeeting });
  };

  // Helper function to get current period group totals
  const getCurrentPeriodGroupTotals = () => {
    return members.length > 0 ? members[0].group_totals : null;
  };

  // "Current" now means "no more recent period exists" (hasNext false for
  // the period on screen), rather than "index 0 in whatever list happened
  // to be loaded" — this stays correct now that navigation windows only
  // hold <=3 periods instead of the group's whole history.
  const isPastPeriod = hasNext;

  // Previous/Next now just hand off to the shared navigateToPeriodId,
  // which re-fetches a fresh <=3-period window from the backend each
  // time (and auto-updates the target period first if it's open/active).
  // No more paging through a pre-loaded array.
  const handlePreviousMonth = async () => {
    if (!hasPrevious || !previousPeriodId || navigating) return;
    await navigateToPeriodId(previousPeriodId);
  };

  const handleNextMonth = async () => {
    if (!hasNext || !nextPeriodId || navigating) return;
    await navigateToPeriodId(nextPeriodId);
  };

  // "Switch to School Fees" / "Switch to Regular" — jumps to the sibling
  // period for the same meeting (opposite account type). Everything else
  // (prev/next, auto-update-if-active, mode context) works exactly the
  // same from there on, just now scoped to that account type.
  const handleSwitchAccountType = async () => {
    if (!counterpartPeriod || navigating) return;
    await navigateToPeriodId(counterpartPeriod.id);
  };

  // ✅ FIXED: Enhanced handleUpdatePerformanceAnalysis - always use mode from context
  const handleUpdatePerformanceAnalysis = async (showLoading = true, modeOverride = null) => {
    try {
      if (showLoading) setUpdating(true);
      
      const updateData = currentPeriod ? {
        period_start: currentPeriod.period_start,
        period_end: currentPeriod.period_end
      } : {};
      
      // ✅ FIX: Get mode from context, use override if provided
      const currentMode = getModeForGroup(groupId);
      const effectiveMode = modeOverride !== null ? modeOverride : currentMode;
      
      console.log('📊 Updating performance analysis with mode:', effectiveMode);
      
      // ✅ Update context if using override
      if (modeOverride !== null && modeOverride !== currentMode) {
        updateSchoolFeesMode(modeOverride);
      }
      
      const periodToRestore = currentPeriod;
      const indexToRestore = currentPeriodIndex;
      
      try {
        // ✅ FIX: Pass mode to update API
        await performanceApi.updatePerformanceAnalysis(groupId, updateData, effectiveMode);
      } catch (updateErr) {
        // ✅ FIX: Pass mode to generate API
        console.log('⚠️ Update failed, generating with mode:', effectiveMode);
        await performanceApi.generatePerformanceAnalysis(groupId, effectiveMode);
      }
      
      // ✅ FIX: Fetch with mode in filters
      const response = await performanceApi.getGroupPerformanceAnalysis(groupId, {
        is_school_fees: effectiveMode
      });
      
      if (response && response.performance_analyses && response.performance_analyses.length > 0) {
        setPerformanceData(response.performance_analyses);
        
        // ✅ Confirm mode from response
        if (response.performance_analyses[0]?.is_school_fees !== undefined) {
          const newMode = response.performance_analyses[0].is_school_fees;
          if (newMode !== effectiveMode && modeOverride === null) {
            console.log('🔄 School fees mode changed:', effectiveMode, '->', newMode);
            updateSchoolFeesMode(newMode);
          }
        }
        
        if (response.periods && response.periods.length > 0) {
          const sortedPeriods = response.periods.sort((a, b) => 
            new Date(b.period_start) - new Date(a.period_start)
          );
          setAvailablePeriods(sortedPeriods);
          
          const restoredPeriodIndex = sortedPeriods.findIndex(p => 
            p.period_start === periodToRestore?.period_start && 
            p.period_end === periodToRestore?.period_end
          );
          
          const finalIndex = restoredPeriodIndex !== -1 ? restoredPeriodIndex : indexToRestore;
          const finalPeriod = sortedPeriods[finalIndex] || sortedPeriods[0];
          
          setCurrentPeriodIndex(finalIndex);
          setCurrentPeriod(finalPeriod);
          
          const currentPeriodMembers = response.performance_analyses
            .filter(item => 
              item.period_start === finalPeriod?.period_start &&
              item.period_end === finalPeriod?.period_end
            )
            .sort((a, b) => {
              const idA = parseInt(a.member_reference_id) || 0;
              const idB = parseInt(b.member_reference_id) || 0;
              return idA - idB;
            });
          
          setMembers(currentPeriodMembers);
          generateSummaryData(currentPeriodMembers);
        }
      }
      
    } catch (err) {
      console.error('Error updating performance analysis:', err);
      if (showLoading) {
        setError('Failed to update performance analysis');
      }
    } finally {
      if (showLoading) setUpdating(false);
    }
  };

  // PDF Export Handler
  const handleExport = async (format, isSchoolFees) => {
    if (!currentPeriod) {
      setError('No period selected for export');
      return;
    }
  
    console.log('🎯 handleExport called with:', { format, isSchoolFees, currentPeriod });
  
    if (format === 'pdf') {
      if (!exportUtils) {
        setError('PDF export utilities not ready');
        return;
      }
  
      await exportUtils.exportToPDF(
        currentPeriod,
        isSchoolFees,  // Pass as SECOND parameter
        () => setPdfGenerating(true),
        (filename) => {
          setPdfGenerating(false);
          console.log(`PDF downloaded successfully: ${filename}`);
        },
        (errorMessage) => {
          setPdfGenerating(false);
          setError(`PDF Export Error: ${errorMessage}`);
        }
      );
    } else if (format === 'excel') {
      if (!excelExportUtils) {
        setError('Excel export utilities not ready');
        return;
      }
  
      await excelExportUtils.exportToExcel(
        currentPeriod,
        isSchoolFees,  // Pass as SECOND parameter
        () => setPdfGenerating(true),
        (filename) => {
          setPdfGenerating(false);
          console.log(`Excel downloaded successfully: ${filename}`);
        },
        (errorMessage) => {
          setPdfGenerating(false);
          setError(`Excel Export Error: ${errorMessage}`);
        }
      );
    }
  };

  // Print Handler — generates the PDF server-side (same backend endpoint
  // as pdfApi elsewhere), then loads it into a hidden iframe and calls
  // the browser's own print() on that iframe. That's what actually gives
  // "the browser's print preview for a PDF" rather than exportUtils'
  // (nonexistent) printPerformanceAnalysis() or just opening a new tab
  // and leaving the user to find the print button themselves.
  const handlePrint = async () => {
    if (!currentPeriod) {
      setError('No period selected for printing');
      return;
    }

    setPrinting(true);
    try {
      const { blob } = await pdfApi.generateAndDownloadPDF(groupId, {
        periodStart: currentPeriod.period_start,
        periodEnd: currentPeriod.period_end,
        isSchoolFees: isSchoolFeesMode
      });

      const url = window.URL.createObjectURL(blob);

      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';

      const cleanup = () => {
        if (iframe.parentNode) document.body.removeChild(iframe);
        window.URL.revokeObjectURL(url);
      };

      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (printErr) {
          console.error('Could not open print dialog on the PDF, opening it in a new tab instead:', printErr);
          window.open(url, '_blank');
        }
        // Can't reliably detect "the print dialog closed", so hold onto
        // the iframe/blob URL for a while before cleaning up rather than
        // yanking it out from under an open print dialog.
        setTimeout(cleanup, 60000);
      };

      iframe.src = url;
      document.body.appendChild(iframe);
    } catch (error) {
      console.error('Print error:', error);
      setError(`Print Error: ${error.message || 'Failed to generate the PDF for printing'}`);
    } finally {
      setPrinting(false);
    }
  };

  // Calculate totals for the table
  const calculateTotals = () => {
    return members.reduce((acc, member) => {
      // Members with a negative LSF are "offset" members (same convention as
      // the PDF backend) - their LSF-linked columns are excluded from the
      // regular totals entirely rather than summed in negative. Total Repaid
      // always counts, but never goes negative.
      const isOffset = (parseFloat(member.lsf) || 0) < 0;
      const totalRepaidValue = parseFloat(member.total_repaid) || 0;

      return {
        totalRepaid: acc.totalRepaid + (totalRepaidValue >= 0 ? totalRepaidValue : 0),
        lsf: acc.lsf + (isOffset ? 0 : (parseFloat(member.lsf) || 0)),
        lsfCF: acc.lsfCF + (parseFloat(member.lsf_cf) || 0),
        instPaid: acc.instPaid + (isOffset ? 0 : (parseFloat(member.inst_paid) || 0)),
        loanCF: acc.loanCF + (isOffset ? 0 : (parseFloat(member.loan_cf) || 0)),
        advaPaid: acc.advaPaid + (isOffset ? 0 : (parseFloat(member.adv_paid) || 0)),
        intPaid: acc.intPaid + (isOffset ? 0 : (parseFloat(member.int_paid) || 0)),
        others: acc.others + (isOffset ? 0 : (parseFloat(member.others) || 0)),
        advCF: acc.advCF + (isOffset ? 0 : (parseFloat(member.adv_cf) || 0))
      };
    }, {
      totalRepaid: 0, lsf: 0, lsfCF: 0, instPaid: 0, loanCF: 0,
      advaPaid: 0, intPaid: 0, others: 0, advCF: 0
    });
  };

  const totals = calculateTotals();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseFloat(amount) || 0);
  };

  // ✅ FIXED: Callback handler for ClosePerformance - use mode from context
  const handleClosePerformanceSuccess = async () => {
    const periodToRestore = currentPeriod;
    const indexToRestore = currentPeriodIndex;
    
    // ✅ FIX: Get mode from context
    const currentMode = getModeForGroup(groupId);
    const filters = {};
    if (currentMode !== undefined) {
      filters.is_school_fees = currentMode;
    }
    
    console.log('🔄 Refreshing after close with mode:', currentMode);
    const response = await performanceApi.getGroupPerformanceAnalysis(groupId, filters);
  
    if (response && response.performance_analyses && response.performance_analyses.length > 0) {
      setPerformanceData(response.performance_analyses);
      
      if (response.periods && response.periods.length > 0) {
        const sortedPeriods = response.periods.sort((a, b) => 
          new Date(b.period_start) - new Date(a.period_start)
        );
        setAvailablePeriods(sortedPeriods);
        
        const restoredPeriodIndex = sortedPeriods.findIndex(p => 
          p.period_start === periodToRestore?.period_start && 
          p.period_end === periodToRestore?.period_end
        );
        
        const finalIndex = restoredPeriodIndex !== -1 ? restoredPeriodIndex : indexToRestore;
        const finalPeriod = sortedPeriods[finalIndex] || sortedPeriods[0];
        
        setCurrentPeriodIndex(finalIndex);
        setCurrentPeriod(finalPeriod);
        
        const currentPeriodMembers = response.performance_analyses
          .filter(item => 
            item.period_start === finalPeriod?.period_start &&
            item.period_end === finalPeriod?.period_end
          )
          .sort((a, b) => {
            const idA = parseInt(a.member_reference_id) || 0;
            const idB = parseInt(b.member_reference_id) || 0;
            return idA - idB;
          });
        
        setMembers(currentPeriodMembers);
        generateSummaryData(currentPeriodMembers);
      }
    }
  };

  const handleClosePerformanceError = (errorMessage) => {
    setError(errorMessage);
  };

  // Where "back" goes depends on how we got here. periodIdParam is only
  // ever set by PerformancePeriods.js's "View Performance" button, so its
  // presence is what "called from Performance Periods" means here.
  // backLinkState is returnTo, echoed straight back unchanged — it already
  // carries the exact row to highlight and the filters that were on
  // screen there, so "back" restores that view instead of resetting it.
  const cameFromPerformancePeriods = Boolean(periodIdParam);
  const backLinkTo = cameFromPerformancePeriods
    ? '/management/groups/performance-periods'
    : '/management/groups';
  const backLinkState = cameFromPerformancePeriods ? returnTo : undefined;

  // A color that reads clearly against every header state below (blue for
  // open, amber for active, dark grey for closed) without being black,
  // white, blue, or orange.
  const BACK_BUTTON_COLOR = '#F06292';

  // Handle loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading performance analysis...</Typography>
      </Box>
    );
  }

  // Handle error state
  if (error && !performanceData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </Box>
    );
  }

  // Get header colors based on current period status
  const headerColors = getHeaderColors(currentPeriod?.status);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header with Dynamic Colors */}
      <Box sx={{
        ...headerColors,
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderRadius: '4px 4px 0 0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <IconButton
            component={RouterLink}
            to={backLinkTo}
            state={backLinkState}
            sx={{ color: BACK_BUTTON_COLOR, mt: '-4px' }}
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h6">
              {group?.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2">
                Period: {currentPeriod?.period_name || 'Loading...'}
              </Typography>
              {currentPeriod?.status && (
                <Chip 
                  label={currentPeriod.status.toUpperCase()} 
                  size="small" 
                  color={
                    currentPeriod.status === 'closed' ? 'secondary' : 
                    currentPeriod.status === 'active' ? 'warning' : 
                    'success'
                  }
                  sx={{ ml: 1 }}
                />
              )}
              {/* ADD THIS CHIP - School Fees Mode Badge */}
            {isSchoolFeesMode && (
              <Chip 
                label="SCHOOL FEES MODE" 
                size="small" 
                color="secondary"
                icon={<School />}
                sx={{ ml: 1 }}
              />
            )}
            {navigating && (
              <Chip 
                label="Auto-updating..." 
                size="small" 
                color="info"
                icon={<CircularProgress size={12} />}
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Previous Period (Auto-updates if open/active)">
            <IconButton
              sx={{ color: 'inherit' }}
              onClick={handlePreviousMonth}
              disabled={!hasPrevious || navigating}
            >
              {navigating ? 
                <CircularProgress size={20} /> : 
                <ChevronLeft />
              }
            </IconButton>
          </Tooltip>
          <Tooltip title="Next Period">
            <IconButton
              sx={{ color: 'inherit' }}
              onClick={handleNextMonth}
              disabled={!hasNext || navigating}
            >
              <ChevronRight />
            </IconButton>
          </Tooltip>
          <Tooltip title={
            counterpartPeriod
              ? `Switch to ${counterpartPeriod.is_school_fees ? 'School Fees' : 'Regular'}`
              : `No ${isSchoolFeesMode ? 'regular' : 'school fees'} period exists for this meeting`
          }>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<School fontSize="small" />}
                onClick={handleSwitchAccountType}
                disabled={!counterpartPeriod || navigating}
                sx={{ color: 'inherit', borderColor: 'currentColor', ml: 1, whiteSpace: 'nowrap' }}
              >
                Switch to {isSchoolFeesMode ? 'Regular' : 'School Fees'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Manual Update Analysis">
            <IconButton
              sx={{ color: 'inherit', ml: 1 }}
              onClick={() => handleUpdatePerformanceAnalysis(true)}
              disabled={updating || navigating}
            >
              {updating ? <CircularProgress size={20} /> : <Update />}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Assessment />}
            onClick={() => setValidationDialogOpen(true)}
            disabled={!currentPeriod || navigating || currentPeriod?.status !== 'active'}  // ADD THIS CONDITION
            sx={{ 
              ml: 2,
              bgcolor: '#10b981',  // emerald green
              color: 'white',
              '&:hover': {
                bgcolor: '#059669'  // darker emerald on hover
              }
            }}
          >
            Run Test
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<LockClock />}
            onClick={() => setConfirmCloseForm(true)}
            disabled={currentPeriod?.status === 'closed' || navigating}
            sx={{ ml: 2, bgcolor: '#9C27B0' }}
          >
            Close Performance Form
          </Button>
          <Tooltip title="Export (PDF/Excel)">
            <IconButton 
              sx={{ color: 'inherit', ml: 1 }} 
              onClick={() => setExportDialogOpen(true)}
              disabled={navigating || pdfGenerating || !exportUtils || !excelExportUtils}
            >
              {pdfGenerating ? <CircularProgress size={20} /> : <Download />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Print">
            <IconButton 
              sx={{ color: 'inherit', ml: 1 }} 
              onClick={handlePrint}
              disabled={navigating || printing || !currentPeriod}
            >
              {printing ? <CircularProgress size={20} /> : <Print />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{ flex: 1, overflow: 'auto' }}>
        <Box sx={{ p: 3 }}>
          {/* Members Table */}
          <MembersTable
            members={members}
            totals={totals}
            formatCurrency={formatCurrency}
            isPastPeriod={isPastPeriod}
            navigating={navigating}
          />

          {/* Financial Summary Section - Cash / Savings+Advance / Loan */}
          {summaryData && (
            <Grid container spacing={3} sx={{ mb: 1 }}>
              <Grid item xs={12} md={4}>
                <CashSummary cashSummary={summaryData.cashSummary} formatCurrency={formatCurrency} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Grid container direction="column" spacing={3}>
                  <Grid item>
                    <SavingsSummary savingsSummary={summaryData.savingsSummary} formatCurrency={formatCurrency} />
                  </Grid>
                  <Grid item>
                    <AdvanceSummary advanceSummary={summaryData.advanceSummary} formatCurrency={formatCurrency} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4}>
                <LoanSummary loanSummary={summaryData.loanSummary} formatCurrency={formatCurrency} />
              </Grid>
            </Grid>
          )}

          {/* Group Report */}
          <GroupReport
            totalCollection={summaryData?.cashSummary?.totalCollection}
            memberCount={members.length}
            loanCF={totals.loanCF}
            advCF={totals.advCF}
            lsf={totals.lsf}
            status={currentPeriod?.status}
            isPastPeriod={isPastPeriod}
            formatCurrency={formatCurrency}
          />

          {/* Next Meeting Details */}
          <NextMeetingDetails nextMeeting={summaryData?.nextMeeting} />
        </Box>
      </Paper>

      <ExportFormatDialog 
  open={exportDialogOpen}
  onClose={() => setExportDialogOpen(false)}
  onExport={handleExport}
  currentPeriod={currentPeriod}
  groupName={group?.name}
  groupId={groupId}  // ✅ ADD THIS LINE - Pass groupId to dialog
/>

      {/* ClosePerformance Dialog Component */}
      <ClosePerformance 
      open={confirmCloseForm}
      onClose={() => setConfirmCloseForm(false)}
      currentPeriod={currentPeriod}
      groupId={groupId}
      onSuccess={handleClosePerformanceSuccess}
      onError={handleClosePerformanceError}
      onUpdatePerformanceAnalysis={handleUpdatePerformanceAnalysis}
      isSchoolFeesMode={isSchoolFeesMode}  // ADD THIS LINE
    />

      {/* Validation Dialog */}
      <Dialog 
        open={validationDialogOpen}
        onClose={() => setValidationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Performance Period Validation
        </DialogTitle>
        <DialogContent>
          {currentPeriod && (
            <PerformanceValidation
              groupId={groupId}
              periodId={currentPeriod.id || currentPeriod.period_id}
              periodName={currentPeriod.period_name}
              onValidationComplete={(report) => {
                console.log('Validation completed:', report);
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PerformanceAnalysis;